import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { requireWorkflowPayloadVersion } from './platform-workflow-definition-lifecycle.models';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  signal,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UiPanelComponent } from '@template-ui/components';

import {
  WorkflowAssignmentPolicy,
  WorkflowAssignmentPolicyScopeCode,
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';
import { WorkflowRegistryDiagnosticDisplayError } from './platform-workflow-registry-diagnostics.models';
import { WorkflowDraftConfirmationService } from './workflow-draft-confirmation.service';
import { WorkflowEditorPendingScope } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

type AssignmentPolicyForm = Omit<
  WorkflowAssignmentPolicy,
  'eligibleRoleCodes' | 'eligibleQueueCodes'
> & {
  eligibleRoleCodes: string[];
  eligibleQueueCodes: string[];
};

interface AssignmentPolicyRow {
  index: number;
  policy: WorkflowAssignmentPolicy | null;
}

interface AssignmentPolicyDisplayField {
  id: string;
  label: string;
  value: string;
  presence: 'absent' | 'null' | 'value';
  raw: unknown;
  issues: readonly string[];
}
interface AssignmentPolicyPresentationRow {
  index: number;
  selection: AssignmentPolicyRow;
  raw: unknown;
  idLabel: string;
  attachmentLabel: string;
  modeLabel: string;
  issues: readonly string[];
  fields: readonly AssignmentPolicyDisplayField[];
}
interface AssignmentPolicyCollectionView {
  presence: 'absent' | 'null' | 'value';
  state: 'absent' | 'empty' | 'available' | 'retained-unknown';
  raw: unknown;
  issues: readonly string[];
  rows: readonly AssignmentPolicyPresentationRow[];
}

const ASSIGNMENT_MODES = [
  { value: 'queue_only', label: 'Shared team inbox' },
  { value: 'claim', label: 'Staff claim work' },
  { value: 'direct', label: 'Assign to a person' },
  { value: 'claim_or_direct', label: 'Staff claim or receive work' },
] as const;

const UNASSIGNED_BEHAVIORS = [
  { value: 'require_queue', label: 'A queue is always required' },
  { value: 'allow_unassigned', label: 'May be completely unassigned' },
] as const;

const FALLBACK_BEHAVIORS = [
  { value: 'block', label: 'Block entry' },
  { value: 'initial_queue', label: 'Return to the initial queue' },
  { value: 'unassigned', label: 'Leave completely unassigned' },
] as const;

const RETURN_BEHAVIORS = [
  {
    value: 'retain_assignment',
    label: 'Restore the prior eligible person and queue',
  },
  {
    value: 'retain_queue',
    label: 'Clear the person and restore the prior queue',
  },
  {
    value: 'initial_queue',
    label: 'Clear the person and use the initial queue',
  },
  { value: 'unassigned', label: 'Clear both person and queue' },
] as const;

const REENTRY_BEHAVIORS = [
  { value: 'retain_assignment', label: 'Retain the person and queue' },
  { value: 'retain_queue', label: 'Clear the person and retain the queue' },
  { value: 'initial_queue', label: 'Reset to the initial queue' },
  { value: 'unassigned', label: 'Clear both person and queue' },
] as const;

const ROLE_ASSIGNMENT_MODES = {
  claim_or_direct: 'Assign from dashboard or let staff claim work',
  direct: 'Assign from dashboard', claim: 'Staff claim work themselves',
} as const;
const knownAssignmentFormat = (version: string | undefined) => Array.from({ length: 23 }, (_, index) => `1.${index}`).includes(version ?? '');
const assignmentRoleLabel = (code: string) => code.replace(/^role[._-]/i, '').replace(/[._-]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());

/** A summary of applied draft setup, independent of a leaf editor's incomplete input. */
export interface WorkflowStepAssignmentSummary {
  state: 'unconfigured' | 'roles' | 'legacy' | 'ambiguous' | 'unsupported';
  roleLabels: readonly string[];
  unavailableRoleCodes: readonly string[];
  modeLabel: string | null;
  description: string;
  readOnly: boolean;
}

/** Current step attachment only; independent workflow policies do not supply defaults. */
export function workflowStepAssignmentSummary(document: WorkflowDefinitionDocument | null, nodeId: string | null, roleCodes: readonly string[],
  options: { canAuthor?: boolean; referenceLoading?: boolean; referenceError?: boolean } = {}): WorkflowStepAssignmentSummary {
  const result: WorkflowStepAssignmentSummary = { state: 'unconfigured', roleLabels: [], unavailableRoleCodes: [], modeLabel: null,
    description: 'Assignment is not configured for this step.', readOnly: options.canAuthor === false };
  const warning = stepAssignmentAttachmentWarning(document, nodeId);
  if (warning) return { ...result, state: 'ambiguous', description: warning, readOnly: true };
  const issue = roleAssignmentWireIssue(document, nodeId);
  if (issue) return { ...result, state: 'unsupported', readOnly: true, description: issue };
  const policy = document?.assignmentPolicies?.find(policy => policy?.scopeCode === 'step' && policy.nodeId === nodeId);
  if (!policy) return result;
  const roles = normalizeCodes(policy.eligibleRoleCodes);
  return { ...result, state: policy.assignmentBasisCode === 'roles' ? 'roles' : 'legacy',
    roleLabels: roles.map(assignmentRoleLabel),
    unavailableRoleCodes: options.referenceLoading || options.referenceError ? [] : roles.filter(code => !roleCodes.includes(code)),
    modeLabel: policy.assignmentBasisCode === 'roles' ? ROLE_ASSIGNMENT_MODES[policy.assignmentModeCode as keyof typeof ROLE_ASSIGNMENT_MODES] ?? `Saved mode · ${policy.assignmentModeCode}`
      : ASSIGNMENT_MODES.find(mode => mode.value === policy.assignmentModeCode)?.label ?? policy.assignmentModeCode,
    description: policy.assignmentBasisCode === 'roles' ? 'First visit starts unassigned. Role membership and existing permissions still apply.' : 'Earlier queue assignment is retained. Review its settings before converting this draft.' };
}

function stepAssignmentAttachmentWarning(document: WorkflowDefinitionDocument | null, nodeId: string | null): string | null {
  const nodes = document?.nodes?.filter(node => node?.id === nodeId) ?? [];
  if (nodes.length !== 1 || nodes[0].typeCode !== 'human_step') return 'This work step is missing, duplicated, or is not an editable human step. Open workflow policies to inspect the saved attachments.';
  if (document?.assignmentPolicies != null && !Array.isArray(document.assignmentPolicies)) return 'The saved assignment collection is unsupported. Its original content is retained.';
  const all = document?.assignmentPolicies ?? [], rows = all.filter(policy => policy?.nodeId === nodeId);
  if (rows.some(policy => policy?.scopeCode !== 'step')) return 'A saved policy names this step with an invalid attachment scope. Repair its attachment in workflow policies before configuring responsibility here.';
  if (rows.length > 1) return 'More than one policy is attached to this step. Resolve the duplicate attachments in workflow policies; no policy has been selected automatically.';
  const policy = rows[0];
  if (policy && (typeof policy.id !== 'string' || !policy.id.trim() || all.filter(row => row?.id === policy.id).length !== 1)) return 'The attached policy has a missing or duplicated stable ID. Repair it in workflow policies before configuring responsibility here.';
  return null;
}

function roleAssignmentWireIssue(document: WorkflowDefinitionDocument | null, nodeId: string | null): string | null {
  if (document && !knownAssignmentFormat(document.payloadSchemaVersion)) return 'This saved workflow format is unsupported. Its assignment settings are retained read-only.';
  if (document?.assignmentPolicies != null && !Array.isArray(document.assignmentPolicies)) return 'The saved assignment collection is unsupported. Its original content is retained read-only.';
  const policy = document?.assignmentPolicies?.find(policy => policy?.nodeId === nodeId);
  if (!policy) return null;
  const keys = ['id', 'scopeCode', 'nodeId', 'initialQueueCode', 'eligibleRoleCodes', 'eligibleQueueCodes', 'assignmentModeCode', 'unassignedBehaviorCode', 'fallbackBehaviorCode', 'returnBehaviorCode', 'reentryBehaviorCode', 'assignmentBasisCode'];
  const codes = (values: unknown) => values == null || (Array.isArray(values) && values.every(value => typeof value === 'string' && !!value && value === value.trim()) && new Set(values).size === values.length);
  if (Object.keys(policy).some(key => !keys.includes(key)) || !codes(policy.eligibleRoleCodes) || !codes(policy.eligibleQueueCodes) ||
    (policy.assignmentBasisCode != null && policy.assignmentBasisCode !== 'roles') ||
    ['id', 'scopeCode', 'initialQueueCode', 'assignmentModeCode', 'unassignedBehaviorCode', 'fallbackBehaviorCode', 'returnBehaviorCode', 'reentryBehaviorCode'].some(key => typeof Reflect.get(policy, key) !== 'string'))
    return 'This saved assignment setup contains unsupported settings. Its original content is retained read-only.';
  return null;
}

@Component({
  selector: 'phms-workflow-assignment-policy',
  standalone: true,
  imports: [UiButtonDirective, UiControlDirective, UiPanelComponent, CommonModule, FormsModule],
  templateUrl: './workflow-assignment-policy.component.html',
  styleUrl: './workflow-assignment-policy.component.scss',
})
export class WorkflowAssignmentPolicyComponent implements OnChanges {
  private static nextInstanceId = 0;
  readonly idPrefix = `assignment-policy-${++WorkflowAssignmentPolicyComponent.nextInstanceId}`;
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() stepNodeId: string | null = null;
  @Input() compact = false;
  @Input() dashboardAssignment = false;
  @Input() allowNewPolicies = true;
  @Input() canAuthor = false;
  @Input() inspectionMode = false;
  @Input() interactionBlocked = false;
  @Input() roleCodes: string[] = [];
  @Input() queueCodes: string[] = [];
  @Input() referenceLoading = false;
  @Input() referenceError: WorkflowRegistryDiagnosticDisplayError | null = null;
  @Input() resetVersion = 0;

  @Output() readonly documentChange =
    new EventEmitter<WorkflowDefinitionDocument>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();
  @Output() readonly retryReferencesRequested = new EventEmitter<void>();
  @Output() readonly manageWorkflowRequested = new EventEmitter<void>();

  @ViewChild('editorHeading') private editorHeading?: ElementRef<HTMLElement>;
  @ViewChild('policyError') private policyError?: ElementRef<HTMLElement>;

  readonly selectedIndex = signal<number | null>(null);
  readonly isAdding = signal(false);
  readonly formDirty = signal(false);
  readonly localError = signal<string | null>(null);
  readonly statusMessage = signal<string | null>(null);
  readonly isEditingStep = signal(false);
  readonly assignmentActionsOpen = signal(false);

  readonly assignmentModes = ASSIGNMENT_MODES;
  readonly unassignedBehaviors = UNASSIGNED_BEHAVIORS;
  readonly fallbackBehaviors = FALLBACK_BEHAVIORS;
  readonly returnBehaviors = RETURN_BEHAVIORS;
  readonly reentryBehaviors = REENTRY_BEHAVIORS;

  formValues: AssignmentPolicyForm = this.emptyForm();
  private lastResetVersion: number | null = null;
  private loadedPolicy: WorkflowAssignmentPolicy | null = null;
  private editorReference = '';
  private loadedAttachment = '';
  private editorOwnerKey = '';
  private focusRequest = 0;
  private confirmationPending = false;
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true });

  constructor(
    private readonly confirmation: WorkflowDraftConfirmationService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.lastResetVersion !== this.resetVersion ||
      changes['stepNodeId'] ||
      changes['compact'] ||
      changes['dashboardAssignment']
    ) {
      this.lastResetVersion = this.resetVersion;
      this.resetEditor(true);
      return;
    }

    if (!this.document) {
      this.resetEditor(true);
      return;
    }

    if (changes['document'] && !this.formDirty()) {
      if (this.stepMode) {
        this.selectedIndex.set(this.stepPolicyRow?.index ?? null);
        this.loadSelectedPolicy();
        this.openCompactEditor();
        return;
      }
      const selected = this.selectedIndex();
      if (selected === null || selected >= this.policyRows.length) {
        this.selectedIndex.set(this.policyRows[0]?.index ?? null);
      }
      this.loadSelectedPolicy();
    }
    this.openCompactEditor();
  }

  get policyRows(): AssignmentPolicyRow[] {
    if (this.document?.assignmentPolicies != null && !Array.isArray(this.document.assignmentPolicies)) return [];
    return (this.document?.assignmentPolicies ?? []).map((policy, index) => ({
      index,
      policy,
    }));
  }

  get policyCollectionView(): AssignmentPolicyCollectionView {
    const document: Record<string, unknown> = assignmentRecord(this.document)
      ? this.document
      : {};
    const raw = document['assignmentPolicies'];
    const presence = assignmentPresence(document, 'assignmentPolicies');
    const selections = this.policyRows;
    const rows = Array.isArray(raw)
      ? raw.map((value, index) => {
          const policy = assignmentRecord(value) ? value : {};
          const attachment = assignmentAttachment(value, this.document);
          const issues = [
            ...assignmentShapeIssues(value),
            ...attachment.issues,
          ];
          return {
            index,
            selection: selections[index],
            raw: structuredClone(value),
            idLabel: savedAssignmentValue(policy['id']),
            attachmentLabel: attachment.label,
            modeLabel: this.assignmentModeLabel(policy['assignmentModeCode']),
            issues,
            fields: assignmentFields.map(([key, label]) => ({
              id: `assignment-${index}:${key}`,
              label,
              presence: assignmentPresence(policy, key),
              value:
                key === 'eligibleRoleCodes' || key === 'eligibleQueueCodes'
                  ? this.savedCodes(policy[key])
                  : savedAssignmentValue(policy[key]),
              raw: structuredClone(policy[key]),
              issues: [],
            })),
          };
        })
      : [];
    const issues =
      presence === 'value' && !Array.isArray(raw)
        ? [
            'The saved assignment policy collection is unsupported. Its original value is retained; no empty policy list is inferred.',
          ]
        : [];
    return {
      presence,
      state:
        presence !== 'value'
          ? 'absent'
          : issues.length || rows.some((row) => row.issues.length)
            ? 'retained-unknown'
            : rows.length
              ? 'available'
              : 'empty',
      raw: structuredClone(raw),
      issues,
      rows,
    };
  }

  get selectedPolicyView(): AssignmentPolicyPresentationRow | null {
    const index = this.selectedIndex();
    return index === null
      ? null
      : (this.policyCollectionView.rows.find((row) => row.index === index) ??
          null);
  }

  /** A presentation shape gate, not validation or a renewed right to apply a stale buffer. */
  get canEditSelectedPolicy(): boolean {
    if (this.inspectionMode) return false;
    const raw =
      this.formDirty() && this.loadedPolicy
        ? this.loadedPolicy
        : this.selectedRow?.policy;
    if (
      assignmentShapeIssues(raw).length ||
      !assignmentRecord(raw) ||
      raw['assignmentBasisCode'] === 'roles'
    )
      return false;
    // Existing pending input remains visible; the existing owner/apply checks still govern settlement.
    if (this.formDirty() && this.loadedPolicy) return true;
    return assignmentAttachment(raw, this.document).issues.length === 0;
  }

  savedCodes(value: unknown): string {
    if (!Array.isArray(value)) return savedAssignmentValue(value);
    return value.length
      ? value.map(savedAssignmentValue).join(', ')
      : 'Empty saved list';
  }

  get selectedRow(): AssignmentPolicyRow | null {
    const index = this.selectedIndex();
    if (this.stepMode && index !== this.stepPolicyRow?.index) return null;
    return index === null
      ? null
      : (this.policyRows.find((row) => row.index === index) ?? null);
  }

  get selectedPolicy(): WorkflowAssignmentPolicy | null {
    return this.selectedRow?.policy ?? null;
  }

  get stepMode(): boolean {
    return this.stepNodeId !== null;
  }
  get compactStepMode(): boolean {
    return this.compact && this.stepMode;
  }

  get selectedStep(): WorkflowDefinitionNode | null {
    return this.selectedStepIn(this.document);
  }
  private selectedStepIn(document: WorkflowDefinitionDocument | null): WorkflowDefinitionNode | null {
    const matches = (document?.nodes ?? []).filter(
      (node) => node?.id === this.stepNodeId,
    );
    return matches.length === 1 && matches[0].typeCode === 'human_step'
      ? matches[0]
      : null;
  }

  get stepLabel(): string {
    return (
      this.selectedStep?.displayName ||
      this.selectedStep?.stepCode ||
      this.stepNodeId ||
      'this step'
    );
  }

  get stepAttachmentWarning(): string | null {
    return this.stepAttachmentWarningIn(this.document);
  }
  private stepAttachmentWarningIn(document: WorkflowDefinitionDocument | null): string | null {
    return this.stepMode ? stepAssignmentAttachmentWarning(document, this.stepNodeId) : null;
  }

  get roleReadOnlyReason(): string | null { return this.dashboardAssignment ? roleAssignmentWireIssue(this.document, this.stepNodeId) : null; }
  get roleInputInvalid(): boolean { return !!this.localError() && (!this.formValues.eligibleRoleCodes.length || this.formValues.eligibleRoleCodes.some(code => !this.roleCodes.includes(code))); }
  get repeatSummary(): string {
    return this.formValues.returnBehaviorCode === 'unassigned' && this.formValues.reentryBehaviorCode === 'unassigned' ? 'Leave unassigned' : 'Custom return and repeat settings';
  }

  get stepPolicyRow(): AssignmentPolicyRow | null {
    if (!this.stepMode || this.stepAttachmentWarning) return null;
    return (
      this.policyRows.find(
        (row) =>
          row.policy?.scopeCode === 'step' &&
          row.policy.nodeId === this.stepNodeId,
      ) ?? null
    );
  }

  get workflowPolicy(): WorkflowAssignmentPolicy | null {
    const matches = this.policyRows.filter(
      (row) => row.policy?.scopeCode === 'workflow',
    );
    const policy = matches.length === 1 ? matches[0].policy : null;
    return policy &&
      !policy.nodeId &&
      typeof policy.id === 'string' && policy.id.trim() &&
      this.policyRows.filter((row) => row.policy?.id === policy.id).length === 1
      ? policy
      : null;
  }

  get workflowPolicySummary(): string {
    const count = this.policyRows.filter(
      (row) => row.policy?.scopeCode === 'workflow',
    ).length;
    return this.workflowPolicy
      ? 'Whole-workflow responsibility is configured. It does not provide responsibility for this step.'
      : count > 0
        ? 'Whole-workflow policies need repair. They do not provide responsibility for this step.'
        : 'No whole-workflow policy is configured. Workflow and step responsibility are separate.';
  }

  get stepReferenceWarning(): string | null {
    if (
      !this.stepMode ||
      !this.selectedStep?.declaration ||
      this.stepAttachmentWarning
    )
      return null;
    if (!this.selectedStep.declaration.behavior)
      return "This step's page behavior is incomplete. Repair its page setup before linking responsibility.";
    const reference =
      this.selectedStep.declaration.behavior?.assignmentPolicyId;
    const attachedId = this.stepPolicyRow?.policy?.id ?? null;
    if (reference === attachedId || (!reference && !attachedId)) return null;
    return reference
      ? `The step's page settings reference '${reference}', which is not its attached responsibility policy. Step responsibility uses the step attachment, not this reference.`
      : 'The step has an attached responsibility policy, but its page settings do not reference it yet.';
  }

  get canCopyWorkflowPolicy(): boolean {
    return (
      this.stepMode &&
      this.canAddPolicy &&
      !!this.workflowPolicy &&
      !this.formDirty()
    );
  }

  get canRepairStepReference(): boolean {
    return !!this.stepPolicyRow && !!this.selectedStep?.declaration?.behavior;
  }

  get visibleQueueCodes(): string[] {
    return [
      ...new Set(
        [
          ...this.queueCodes,
          ...this.formValues.eligibleQueueCodes,
          this.formValues.initialQueueCode,
        ].filter(Boolean),
      ),
    ];
  }

  get visibleRoleCodes(): string[] {
    return [
      ...new Set(
        [...this.roleCodes, ...this.formValues.eligibleRoleCodes].filter(
          Boolean,
        ),
      ),
    ];
  }

  knownChoice(value: string, choices: readonly { value: string }[]): boolean {
    return choices.some((choice) => choice.value === value);
  }

  assignmentModeLabel(value: unknown): string {
    return (
      ASSIGNMENT_MODES.find((choice) => choice.value === value)?.label ??
      savedAssignmentValue(value)
    );
  }

  get humanSteps(): WorkflowDefinitionNode[] {
    const nodes = this.document?.nodes;
    return (Array.isArray(nodes) ? nodes : [])
      .filter(
        (node) =>
          node?.typeCode === 'human_step' && typeof node.id === 'string',
      )
      .sort(
        (left, right) =>
          left.displayOrder - right.displayOrder ||
          left.id.localeCompare(right.id),
      );
  }

  get referenceDataReady(): boolean {
    return this.dashboardAssignment
      ? this.roleCodes.length > 0
      : this.queueCodes.length > 0;
  }

  get legacyStepPolicy(): WorkflowAssignmentPolicy | null {
    const policy = this.stepPolicyRow?.policy;
    return policy &&
      policy.assignmentBasisCode !== 'roles' &&
      this.formValues.assignmentBasisCode !== 'roles'
      ? policy
      : null;
  }

  get assignmentControlsDisabled(): boolean {
    return !this.canAuthor || this.inspectionMode || this.interactionBlocked || this.referenceLoading ||
      (this.dashboardAssignment && (!!this.referenceError || !!this.roleReadOnlyReason));
  }

  roleLabel(code: string): string { return assignmentRoleLabel(code); }

  convertToRoleAssignment(): void {
    if (
      !this.legacyStepPolicy ||
      this.assignmentControlsDisabled ||
      this.formDirty()
    )
      return;
    if (!this.adoptCurrentDocument(this.latestDocument ? this.latestDocument() : this.document)) return;
    this.selectedIndex.set(this.stepPolicyRow!.index);
    this.loadSelectedPolicy();
    this.formValues = {
      ...this.formValues,
      assignmentBasisCode: 'roles',
      initialQueueCode: '',
      eligibleQueueCodes: [],
      eligibleRoleCodes: [...this.formValues.eligibleRoleCodes],
      assignmentModeCode: 'claim_or_direct',
      unassignedBehaviorCode: 'allow_unassigned',
      fallbackBehaviorCode: 'unassigned',
      returnBehaviorCode: 'unassigned',
      reentryBehaviorCode: 'unassigned',
    };
    this.onFormChange();
  }

  get canAddPolicy(): boolean {
    if (
      !this.canAuthor ||
      this.inspectionMode ||
      (this.document?.assignmentPolicies != null &&
        !Array.isArray(this.document.assignmentPolicies)) ||
      !this.allowNewPolicies ||
      this.interactionBlocked ||
      this.referenceLoading ||
      !this.referenceDataReady
    ) {
      return false;
    }
    if (this.stepMode)
      return !this.stepAttachmentWarning && !this.stepPolicyRow;
    const policies = this.policyRows.map((row) => row.policy).filter(isPolicy);
    const workflowAvailable = !policies.some(
      (policy) => policy.scopeCode === 'workflow',
    );
    const stepAvailable = this.humanSteps.some(
      (step) =>
        !policies.some(
          (policy) => policy.scopeCode === 'step' && policy.nodeId === step.id,
        ),
    );
    return workflowAvailable || stepAvailable;
  }

  selectPolicy(row: AssignmentPolicyRow, focus = false): void {
    if (
      this.formDirty() ||
      this.interactionBlocked ||
      (this.stepMode && row.index !== this.stepPolicyRow?.index)
    ) {
      return;
    }
    this.selectedIndex.set(row.index);
    this.isAdding.set(false);
    this.loadSelectedPolicy();
    if (focus) {
      this.scheduleFocus(() => this.editorHeading?.nativeElement);
    }
  }

  beginAddPolicy(): void {
    if (!this.canAddPolicy || this.formDirty()) {
      return;
    }
    this.prepareNewPolicy(true);
    this.scheduleFocus(() => this.editorHeading?.nativeElement);
  }

  private prepareNewPolicy(dirty: boolean): void {
    this.captureEditorReference();
    const policies = this.policyRows.map((row) => row.policy).filter(isPolicy);
    const workflowAvailable = !policies.some(
      (policy) => policy.scopeCode === 'workflow',
    );
    const firstAvailableStep = this.humanSteps.find(
      (step) =>
        !policies.some(
          (policy) => policy.scopeCode === 'step' && policy.nodeId === step.id,
        ),
    );
    const scopeCode: WorkflowAssignmentPolicyScopeCode = this.stepMode
      ? 'step'
      : workflowAvailable
        ? 'workflow'
        : 'step';
    const nodeId = this.stepMode
      ? this.stepNodeId
      : scopeCode === 'step'
        ? (firstAvailableStep?.id ?? null)
        : null;
    this.selectedIndex.set(null);
    this.loadedPolicy = null;
    this.isAdding.set(true);
    this.formValues = {
      ...this.emptyForm(),
      id: this.nextPolicyId(scopeCode, nodeId),
      scopeCode,
      nodeId,
      assignmentModeCode: this.stepMode ? 'queue_only' : 'claim',
      ...(this.dashboardAssignment
        ? {
            assignmentBasisCode: 'roles' as const,
            assignmentModeCode: 'claim_or_direct',
            unassignedBehaviorCode: 'allow_unassigned',
            fallbackBehaviorCode: 'unassigned',
            returnBehaviorCode: 'unassigned',
            reentryBehaviorCode: 'unassigned',
          }
        : {}),
    };
    this.isEditingStep.set(this.stepMode);
    this.setDirty(dirty);
    this.localError.set(null);
    this.statusMessage.set(null);
  }

  onFormChange(): void {
    if (!this.canAuthor || this.interactionBlocked || (this.dashboardAssignment && this.assignmentControlsDisabled)) {
      return;
    }
    if (this.formValues.scopeCode === 'workflow') {
      this.formValues.nodeId = null;
    }
    this.setDirty(true);
    this.localError.set(null);
    this.statusMessage.set(null);
    if (this.compactStepMode) {
      const error = this.referenceLoading
        ? this.dashboardAssignment
          ? 'Wait for the current role choices to load.'
          : 'Wait for the current queue and role choices to load.'
        : this.validatePolicy(this.normalizedFormPolicy());
      if (error) this.localError.set(error);
      else this.applyPolicy();
    }
  }

  toggleEligibleQueue(code: string, selected: boolean): void {
    if (
      !this.canAuthor ||
      this.interactionBlocked ||
      (!selected && code === this.formValues.initialQueueCode)
    )
      return;
    this.formValues.eligibleQueueCodes = selected
      ? [...new Set([...this.formValues.eligibleQueueCodes, code])]
      : this.formValues.eligibleQueueCodes.filter((value) => value !== code);
    this.onFormChange();
  }

  toggleEligibleRole(code: string, selected: boolean): void {
    if (
      !this.canAuthor ||
      this.interactionBlocked ||
      (this.dashboardAssignment && this.assignmentControlsDisabled) ||
      (selected && this.formValues.assignmentModeCode === 'queue_only')
    )
      return;
    this.formValues.eligibleRoleCodes = selected
      ? [...new Set([...this.formValues.eligibleRoleCodes, code])]
      : this.formValues.eligibleRoleCodes.filter((value) => value !== code);
    this.onFormChange();
  }

  onAssignmentModeChange(): void {
    if (!this.canAuthor || this.interactionBlocked) return;
    if (this.formValues.assignmentModeCode === 'queue_only')
      this.formValues.eligibleRoleCodes = [];
    this.onFormChange();
  }

  onInitialQueueChange(): void {
    if (!this.canAuthor || this.interactionBlocked) return;
    const queue = this.formValues.initialQueueCode;
    if (
      queue &&
      this.queueCodes.includes(queue) &&
      !this.formValues.eligibleQueueCodes.includes(queue)
    ) {
      this.formValues.eligibleQueueCodes = [
        ...this.formValues.eligibleQueueCodes,
        queue,
      ];
    }
    this.onFormChange();
  }

  beginEditStepPolicy(): void {
    if (
      !this.stepMode ||
      !this.stepPolicyRow ||
      !this.canAuthor ||
      this.interactionBlocked ||
      this.formDirty()
    )
      return;
    this.selectedIndex.set(this.stepPolicyRow.index);
    this.loadSelectedPolicy();
    this.isEditingStep.set(true);
    this.focusPendingChanges();
  }

  copyWorkflowSettings(): void {
    const source = this.workflowPolicy;
    if (!this.canCopyWorkflowPolicy || !source) return;
    this.beginAddPolicy();
    this.formValues = {
      ...this.toForm(source),
      id: this.nextPolicyId('step', this.stepNodeId),
      scopeCode: 'step',
      nodeId: this.stepNodeId,
    };
    if (this.compactStepMode) this.onFormChange();
    else
      this.statusMessage.set(
        'Copied workflow settings into an independent policy for this step. Later workflow-policy changes will not change this copy.',
      );
  }

  repairStepReference(): void {
    const policy = this.stepPolicyRow?.policy;
    if (
      !this.document ||
      !policy ||
      !this.canRepairStepReference ||
      !this.stepReferenceWarning ||
      !this.canAuthor ||
      this.interactionBlocked ||
      this.formDirty()
    )
      return;
    const next = this.withStepReference(this.document, policy.id);
    this.document = next;
    this.documentChange.emit(next);
    this.statusMessage.set(
      "The step's page settings now reference its attached policy. Save the draft to persist this repair.",
    );
  }

  private documentReference(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document?.workflowCode, document?.definitionVersionCode, document?.packReference, this.stepNodeId, this.resetVersion, this.compact, this.dashboardAssignment]);
  }
  private attachmentIn(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify(this.selectedStepIn(document)?.declaration?.behavior?.assignmentPolicyId ?? null);
  }
  private ownerKey(): string { return JSON.stringify([this.ownerContext?.()]); }
  private captureEditorReference(): void {
    this.editorOwnerKey = this.ownerKey();
    this.editorReference = this.documentReference(this.document);
    this.loadedAttachment = this.attachmentIn(this.document);
  }
  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.latestDocument ? this.latestDocument() : this.document): readonly WorkflowEditorPendingScope[] {
    const target = (document: WorkflowDefinitionDocument) => (document.assignmentPolicies ?? []).map((policy, index) => ({ policy, index }))
      .filter(row => !!this.loadedPolicy?.id && row.policy?.id === this.loadedPolicy.id);
    return [{
      id: 'assignment-policy', label: this.stepMode ? 'step responsibility input' : 'workflow assignment policy input',
      state: () => {
        if (!this.formDirty()) return 'clean';
        const latest = latestDocument();
        if (!latest || (this.ownerContext && this.ownerKey() !== this.editorOwnerKey) || this.documentReference(latest) !== this.editorReference ||
          (this.stepMode && this.attachmentIn(latest) !== this.loadedAttachment)) return 'stale';
        if (!knownAssignmentFormat(latest.payloadSchemaVersion) || !Array.isArray(latest.assignmentPolicies ?? []) ||
          (this.dashboardAssignment && roleAssignmentWireIssue(latest, this.stepNodeId))) return 'invalid';
        const matches = target(latest);
        if (!this.isAdding() && (matches.length !== 1 || JSON.stringify(matches[0].policy) !== JSON.stringify(this.loadedPolicy))) return 'stale';
        if (!this.canAuthor || this.inspectionMode || this.interactionBlocked || (this.isAdding() && !this.allowNewPolicies) ||
          (this.compactStepMode && this.referenceLoading) || !knownAssignmentFormat(latest.payloadSchemaVersion) ||
          (this.dashboardAssignment && (!!this.referenceError || !!roleAssignmentWireIssue(latest, this.stepNodeId)))) return 'invalid';
        return this.validatePolicy(this.normalizedFormPolicy(), latest, matches[0]?.index ?? null) ? 'invalid' : 'valid';
      },
      fingerprint: () => {
        const latest = latestDocument();
        return JSON.stringify([this.formDirty(), this.isAdding(), this.formValues, this.loadedPolicy, this.editorReference, this.loadedAttachment,
          this.documentReference(latest), latest?.assignmentPolicies, latest?.nodes.map(node => [node.id, node.typeCode, node.declaration?.behavior?.assignmentPolicyId]),
          this.roleCodes, this.queueCodes, this.referenceLoading, this.referenceError,
          this.canAuthor, this.inspectionMode, this.interactionBlocked, this.allowNewPolicies, this.editorOwnerKey, this.ownerKey(), latest?.payloadSchemaVersion]);
      },
      apply: () => { const latest = latestDocument(); return latest ? this.applyPendingChanges(latest) : !this.formDirty(); },
      discard: () => {
        const latest = latestDocument(), id = this.loadedPolicy?.id;
        this.document = latest;
        if (!this.stepMode && id) {
          const policies = Array.isArray(latest?.assignmentPolicies) ? latest.assignmentPolicies : [];
          const matches = policies.map((policy, index) => ({ policy, index })).filter(row => row.policy?.id === id);
          this.selectedIndex.set(matches.length === 1 ? matches[0].index : null);
        }
        this.discardPendingChanges();
      },
      focus: () => this.focusPendingChanges(),
    }];
  }
  private adoptCurrentDocument(document: WorkflowDefinitionDocument | null): boolean {
    if (!document || this.destroyRef.destroyed || (this.ownerContext && this.ownerKey() !== this.editorOwnerKey) ||
      this.documentReference(document) !== this.editorReference || (this.stepMode && this.attachmentIn(document) !== this.loadedAttachment)) {
      this.showError('The assignment owner or attachment changed. Your input is retained; reopen the current step before applying it.'); return false;
    }
    if (!knownAssignmentFormat(document.payloadSchemaVersion) || !Array.isArray(document.assignmentPolicies ?? []) || (this.dashboardAssignment && roleAssignmentWireIssue(document, this.stepNodeId))) {
      this.showError('These saved assignment settings are unsupported. Their original content and your input are retained.'); return false;
    }
    if (!this.isAdding()) {
      const matches = (document.assignmentPolicies ?? []).map((policy, index) => ({ policy, index })).filter(row => !!this.loadedPolicy?.id && row.policy?.id === this.loadedPolicy.id);
      if (matches.length !== 1 || JSON.stringify(matches[0].policy) !== JSON.stringify(this.loadedPolicy)) {
        this.showError('The selected assignment policy changed while this form was open. Reset to review the latest policy; your edits remain open.'); return false;
      }
      this.selectedIndex.set(matches[0].index);
    }
    this.document = document; return true;
  }
  applyPendingChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    if (!this.formDirty()) return true;
    if (!this.canAuthor || this.interactionBlocked || this.inspectionMode) return false;
    this.applyPolicy(latestDocument);
    return !this.formDirty();
  }

  applyPolicy(latestDocument?: WorkflowDefinitionDocument): void {
    const document = latestDocument ?? (this.latestDocument ? this.latestDocument() : this.document);
    if (
      !document ||
      !this.canAuthor || this.inspectionMode ||
      this.interactionBlocked ||
      (this.isAdding() && !this.allowNewPolicies) ||
      !this.formDirty()
    ) {
      return;
    }
    if (!this.adoptCurrentDocument(document)) return;
    if (this.dashboardAssignment && this.referenceError) { this.showError('Role choices could not be verified. Retry role choices before applying this edit.'); return; }
    if (this.compactStepMode && this.referenceLoading) {
      this.showError(
        this.dashboardAssignment
          ? 'Wait for the current role choices to load.'
          : 'Wait for the current queue and role choices to load.',
      );
      return;
    }
    if (
      !this.isAdding() &&
      (!this.loadedPolicy ||
        JSON.stringify(this.selectedPolicy) !==
          JSON.stringify(this.loadedPolicy))
    ) {
      this.showError(
        'The selected assignment policy changed while this form was open. Reset to review the latest policy; your edits remain open.',
      );
      return;
    }

    const policy = this.normalizedFormPolicy();
    const error = this.validatePolicy(policy);
    if (error) {
      this.showError(error);
      return;
    }

    const policies = [...(document.assignmentPolicies ?? [])];
    let selectedIndex = this.selectedIndex();
    if (this.isAdding()) {
      policies.push(policy);
      selectedIndex = policies.length - 1;
    } else if (selectedIndex !== null) {
      policies[selectedIndex] = policy;
    } else {
      return;
    }

    let nextDocument: WorkflowDefinitionDocument = {
      ...document,
      payloadSchemaVersion: requireWorkflowPayloadVersion(
        document.payloadSchemaVersion,
        policy.assignmentBasisCode === 'roles' ? '1.13' : '1.2',
      ),
      assignmentPolicies: policies,
    };
    if (this.stepMode)
      nextDocument = this.withStepReference(nextDocument, policy.id);
    if (
      policy.assignmentBasisCode === 'roles' &&
      this.loadedPolicy?.assignmentBasisCode !== 'roles'
    ) {
      nextDocument = {
        ...nextDocument,
        nodes: nextDocument.nodes.map((node) =>
          node?.id === policy.nodeId && node.declaration
            ? {
                ...node,
                declaration: {
                  ...node.declaration,
                  dashboards: node.declaration.dashboards?.map((dashboard) => ({
                    ...dashboard,
                    queueScope: null,
                    columns: dashboard.columns.filter(
                      (field) => field !== 'queueCode',
                    ),
                    ...(dashboard.presentation
                      ? {
                          presentation: {
                            ...dashboard.presentation,
                            columns: [
                              ...dashboard.presentation.columns.filter(
                                (column) => column.field !== 'queueCode',
                              ),
                              ...(!dashboard.presentation.columns.some(
                                (column) =>
                                  column.field === 'assigneeDisplayName',
                              )
                                ? [
                                    {
                                      field: 'assigneeDisplayName',
                                      label: 'Assigned to',
                                    },
                                  ]
                                : []),
                            ],
                          },
                        }
                      : {}),
                  })),
                },
              }
            : node,
        ),
      };
    }
    this.document = nextDocument;
    this.selectedIndex.set(selectedIndex);
    this.isAdding.set(false);
    this.isEditingStep.set(this.compactStepMode);
    this.formValues = this.toForm(policy);
    this.loadedPolicy = JSON.parse(
      JSON.stringify(policy),
    ) as WorkflowAssignmentPolicy;
    this.captureEditorReference();
    this.setDirty(false);
    this.localError.set(null);
    this.statusMessage.set(
      this.compactStepMode
        ? null
        : `Assignment policy '${policy.id}' applied locally. Save the draft to persist it.`,
    );
    this.documentChange.emit(nextDocument);
  }

  resetPendingChanges(): void {
    if (this.interactionBlocked || this.confirmationPending || this.destroyRef.destroyed) return;
    const latest = this.latestDocument ? this.latestDocument() : this.document;
    if (!latest || (this.ownerContext && this.ownerKey() !== this.editorOwnerKey) || this.documentReference(latest) !== this.editorReference) {
      this.showError('The assignment owner changed. Your input is retained.'); return;
    }
    this.document = latest;
    this.discardPendingChanges();
    if (this.dashboardAssignment) this.scheduleFocus(() => this.element.nativeElement.querySelector<HTMLElement>('[data-role-checkbox]:checked:not(:disabled)') ?? this.element.nativeElement.querySelector<HTMLElement>('[data-role-checkbox]:not(:disabled)') ?? this.editorHeading?.nativeElement);
    this.statusMessage.set(
      this.compactStepMode
        ? null
        : 'Open assignment-policy changes were reset.',
    );
  }

  discardPendingChanges(): void {
    this.isAdding.set(false);
    this.isEditingStep.set(false);
    this.setDirty(false);
    this.localError.set(null);
    if (this.stepMode)
      this.selectedIndex.set(this.stepPolicyRow?.index ?? null);
    else if (this.selectedIndex() === null) {
      this.selectedIndex.set(this.policyRows[0]?.index ?? null);
    }
    this.loadSelectedPolicy();
    this.openCompactEditor();
  }

  removeSelectedPolicy(): void {
    if (!this.document || !this.canAuthor || this.inspectionMode || this.interactionBlocked || this.formDirty() ||
      this.confirmationPending || this.ownerViews?.focusSuspended() || this.destroyRef.destroyed || this.roleReadOnlyReason) return;
    const latest = this.latestDocument ? this.latestDocument() : this.document;
    if (!latest || !knownAssignmentFormat(latest.payloadSchemaVersion) ||
      (this.ownerContext && this.ownerKey() !== this.editorOwnerKey) || this.documentReference(latest) !== this.editorReference) {
      this.showError('The assignment owner changed. Review the current setup before removing it.'); return;
    }
    if (this.dashboardAssignment && !this.adoptCurrentDocument(latest)) return;
    this.document = latest;
    const row = this.selectedRow;
    if (!row) return;
    const label = row.policy?.id || `invalid entry ${row.index + 1}`;
    const reference = this.documentReference(latest), owner = this.ownerKey(), attachment = this.attachmentIn(latest);
    const policySnapshot = JSON.stringify(row.policy), form = JSON.stringify(this.formValues);
    this.assignmentActionsOpen.set(false);
    this.confirmationPending = true;
    const release = this.ownerViews?.suspendFocus() ?? (() => undefined);
    void this.confirmation.confirm({
      heading: this.dashboardAssignment ? 'Remove assignment setup?' : 'Remove assignment policy?',
      message: this.dashboardAssignment
        ? `Remove assignment roles for ${this.stepLabel}? Staff will be unable to assign work from this step's dashboards until assignment roles are configured again. Published versions keep their settings.`
        : this.stepMode ? `Remove responsibility for ${this.stepLabel}? This step will have no attached policy; it will not inherit the whole-workflow policy. Running workflows keep their published settings.`
          : `Remove '${label}' from the local workflow document? Runtime assignment is not changed by this authoring action.`,
      confirmText: this.dashboardAssignment ? 'Remove setup' : 'Remove policy', cancelText: this.dashboardAssignment ? 'Keep setup' : 'Keep policy',
    }).then(confirmed => {
      release(); this.confirmationPending = false;
      if (!confirmed) { if (this.dashboardAssignment) this.scheduleFocus(() => this.element.nativeElement.querySelector<HTMLElement>('[data-assignment-actions] summary') ?? undefined); return; }
      const current = this.latestDocument ? this.latestDocument() : this.document;
      if (!this.canAuthor || this.inspectionMode || this.interactionBlocked || this.formDirty() || this.destroyRef.destroyed || !current) return;
      if (!knownAssignmentFormat(current.payloadSchemaVersion) || !Array.isArray(current.assignmentPolicies ?? []) ||
        (this.dashboardAssignment && roleAssignmentWireIssue(current, this.stepNodeId))) {
        this.showError('Assignment settings changed while confirmation was open and are now unsupported. Their original content is retained.'); return;
      }
      if (this.ownerKey() !== owner || this.documentReference(current) !== reference || this.attachmentIn(current) !== attachment || JSON.stringify(this.formValues) !== form) {
        this.showError('The selected policy changed while confirmation was open. Review it before removing.'); return;
      }
      const policies = [...(current.assignmentPolicies ?? [])];
      const matches = this.dashboardAssignment ? policies.map((policy, index) => ({ policy, index })).filter(candidate => candidate.policy?.id === row.policy?.id) : [{ policy: policies[row.index], index: row.index }];
      if (matches.length !== 1 || JSON.stringify(matches[0].policy) !== policySnapshot ||
        (this.dashboardAssignment && (this.stepAttachmentWarningIn(current) || this.selectedPolicy?.id !== row.policy?.id)) ||
        (!this.dashboardAssignment && this.selectedIndex() !== row.index)) {
        this.showError('The selected policy changed while confirmation was open. Review it before removing.'); return;
      }
      policies.splice(matches[0].index, 1);
      let next: WorkflowDefinitionDocument = { ...current, assignmentPolicies: policies };
      if (this.stepMode && this.selectedStepIn(current)?.declaration?.behavior?.assignmentPolicyId === row.policy?.id) next = this.withStepReference(next, null);
      this.document = next; this.documentChange.emit(next);
      this.selectedIndex.set(this.stepMode ? null : policies.length ? Math.min(row.index, policies.length - 1) : null);
      this.isEditingStep.set(false); this.loadSelectedPolicy(); this.openCompactEditor();
      this.statusMessage.set(`Assignment policy '${label}' removed locally. Save the draft to persist it.`);
    }).finally(() => { release(); this.confirmationPending = false; });
  }

  closeAssignmentActions(event: Event): void {
    event.preventDefault(); event.stopPropagation(); this.assignmentActionsOpen.set(false);
    this.scheduleFocus(() => this.element.nativeElement.querySelector<HTMLElement>('[data-assignment-actions] summary') ?? undefined);
  }
  private scheduleFocus(target: () => HTMLElement | undefined): void {
    const request = ++this.focusRequest, owner = this.editorOwnerKey, reference = this.documentReference(this.document);
    setTimeout(() => {
      const latest = this.latestDocument ? this.latestDocument() : this.document;
      if (request !== this.focusRequest || this.destroyRef.destroyed || owner !== this.ownerKey() || reference !== this.documentReference(latest) ||
        this.ownerViews?.focusSuspended() || this.element.nativeElement.closest('[hidden],[inert]')) return;
      target()?.focus();
    });
  }
  focusPendingChanges(): void {
    if (this.stepMode && (this.formDirty() || this.selectedPolicy)) this.isEditingStep.set(true);
    this.scheduleFocus(() => (this.localError() ? this.policyError : this.editorHeading)?.nativeElement);
  }

  focusPolicyPath(pathOrId: string | null | undefined): void {
    if (this.formDirty()) { this.focusPendingChanges(); return; }
    const idFromPath = pathOrId?.match(/assignmentPolicies\[id=([^\]]+)\]/)?.[1];
    const id = idFromPath ?? pathOrId ?? null;
    const matches = id ? this.policyRows.filter(candidate => candidate.policy?.id === id) : this.policyRows.slice(0, 1);
    const row = matches.length === 1 ? matches[0] : null;
    if (row && (!this.stepMode || row.index === this.stepPolicyRow?.index)) {
      this.selectedIndex.set(row.index); this.isAdding.set(false); this.loadSelectedPolicy(); this.isEditingStep.set(this.stepMode);
    }
    this.scheduleFocus(() => this.editorHeading?.nativeElement);
  }

  attachmentLabel(policy: unknown): string {
    return assignmentAttachment(policy, this.document).label;
  }

  codeLabel(value: unknown): string {
    if (typeof value !== 'string') return savedAssignmentValue(value);
    return value
      ? value
          .split(/[_-]/)
          .filter(Boolean)
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ')
      : 'Not recorded';
  }

  trackRow(_index: number, row: { index: number }): number {
    return row.index;
  }

  trackCode(_index: number, code: string): string {
    return code;
  }

  private resetEditor(clearSelection: boolean): void {
    this.focusRequest++; this.assignmentActionsOpen.set(false);
    this.isAdding.set(false);
    this.isEditingStep.set(false);
    this.setDirty(false);
    this.localError.set(null);
    this.statusMessage.set(null);
    if (this.stepMode)
      this.selectedIndex.set(this.stepPolicyRow?.index ?? null);
    else if (
      clearSelection ||
      !this.policyRows.some((row) => row.index === this.selectedIndex())
    ) {
      this.selectedIndex.set(this.policyRows[0]?.index ?? null);
    }
    this.loadSelectedPolicy();
    this.openCompactEditor();
  }

  private openCompactEditor(): void {
    if (
      !this.compactStepMode ||
      !this.document ||
      this.stepAttachmentWarning ||
      this.formDirty()
    )
      return;
    if (this.selectedPolicy) this.isEditingStep.set(true);
    else if (this.canAddPolicy) this.prepareNewPolicy(false);
  }

  private loadSelectedPolicy(): void {
    this.captureEditorReference();
    const policy = this.selectedPolicy;
    this.formValues = policy ? this.toForm(policy) : this.emptyForm();
    this.loadedPolicy = policy
      ? (JSON.parse(JSON.stringify(policy)) as WorkflowAssignmentPolicy)
      : null;
  }

  private emptyForm(): AssignmentPolicyForm {
    return {
      id: '',
      scopeCode: 'workflow',
      nodeId: null,
      initialQueueCode: '',
      eligibleRoleCodes: [],
      eligibleQueueCodes: [],
      assignmentModeCode: 'claim',
      unassignedBehaviorCode: 'require_queue',
      fallbackBehaviorCode: 'initial_queue',
      returnBehaviorCode: 'retain_queue',
      reentryBehaviorCode: 'initial_queue',
    };
  }

  private toForm(policy: WorkflowAssignmentPolicy): AssignmentPolicyForm {
    return {
      ...policy,
      eligibleRoleCodes: normalizeCodes(policy.eligibleRoleCodes),
      eligibleQueueCodes: normalizeCodes(policy.eligibleQueueCodes),
    };
  }

  private normalizedFormPolicy(): WorkflowAssignmentPolicy {
    return {
      ...this.formValues,
      id:
        typeof this.formValues.id === 'string' ? this.formValues.id.trim() : '',
      nodeId:
        this.formValues.scopeCode === 'step' ? this.formValues.nodeId : null,
      initialQueueCode:
        typeof this.formValues.initialQueueCode === 'string'
          ? this.formValues.initialQueueCode.trim()
          : '',
      eligibleRoleCodes: normalizeCodes(this.formValues.eligibleRoleCodes),
      eligibleQueueCodes: normalizeCodes(this.formValues.eligibleQueueCodes),
    };
  }

  private validatePolicy(policy: WorkflowAssignmentPolicy, document = this.document, selectedIndex = this.selectedIndex()): string | null {
    const step = this.selectedStepIn(document), attachmentWarning = this.stepAttachmentWarningIn(document);
    if (this.stepMode && attachmentWarning)
      return attachmentWarning;
    if (
      this.stepMode &&
      step?.declaration &&
      !step.declaration.behavior
    )
      return "Repair this step's incomplete page behavior before linking responsibility.";
    if (
      this.stepMode &&
      (policy.scopeCode !== 'step' || policy.nodeId !== this.stepNodeId)
    ) {
      return 'Responsibility here can only be attached to the selected work step.';
    }
    if (policy.scopeCode !== 'workflow' && policy.scopeCode !== 'step')
      return 'Choose a supported attachment scope.';
    if (
      policy.returnBehaviorCode === 'retain_assignment' &&
      policy.scopeCode !== 'step'
    )
      return 'Restoring a prior person and queue requires a specific work step. Choose a work step or another return behavior.';
    const roleBased = policy.assignmentBasisCode === 'roles';
    const roleReentryChoices = [
      { value: 'unassigned' },
      { value: 'retain_assignment' },
    ];
    const choices = roleBased
      ? ([
          [
            policy.assignmentModeCode,
            ASSIGNMENT_MODES.filter((choice) => choice.value !== 'queue_only'),
            'assignment mode',
          ],
          [policy.returnBehaviorCode, roleReentryChoices, 'return behavior'],
          [policy.reentryBehaviorCode, roleReentryChoices, 're-entry behavior'],
        ] as const)
      : ([
          [policy.assignmentModeCode, ASSIGNMENT_MODES, 'assignment mode'],
          [
            policy.unassignedBehaviorCode,
            UNASSIGNED_BEHAVIORS,
            'unassigned posture',
          ],
          [policy.fallbackBehaviorCode, FALLBACK_BEHAVIORS, 'entry fallback'],
          [policy.returnBehaviorCode, RETURN_BEHAVIORS, 'return behavior'],
          [policy.reentryBehaviorCode, REENTRY_BEHAVIORS, 're-entry behavior'],
        ] as const);
    for (const [value, options, label] of choices) {
      if (!this.knownChoice(value, options))
        return `Choose a supported ${label}. The saved value '${value ?? 'missing'}' is not supported.`;
    }
    if (!policy.id) {
      return 'Enter a stable policy ID.';
    }
    const otherPolicies = (document?.assignmentPolicies ?? []).map((policy, index) => ({ policy, index }))
      .filter((row) => this.isAdding() || row.index !== selectedIndex)
      .map((row) => row.policy)
      .filter(isPolicy);
    if (otherPolicies.some((candidate) => candidate.id === policy.id)) {
      return `Policy ID '${policy.id}' is already used. Stable IDs must be unique.`;
    }
    if (policy.scopeCode === 'step' && !policy.nodeId) {
      return 'Choose the human work step this policy applies to.';
    }
    if (
      policy.scopeCode === 'step' &&
      !(document?.nodes ?? []).some((step) => step.typeCode === 'human_step' && step.id === policy.nodeId)
    ) {
      return 'The selected policy attachment is not an existing human work step.';
    }
    if (
      otherPolicies.some(
        (candidate) =>
          candidate.scopeCode === policy.scopeCode &&
          (policy.scopeCode === 'workflow' ||
            candidate.nodeId === policy.nodeId),
      )
    ) {
      return policy.scopeCode === 'workflow'
        ? 'Only one workflow-wide assignment policy is allowed.'
        : 'Only one assignment policy may attach to the same work step.';
    }
    const eligibleQueues = normalizeCodes(policy.eligibleQueueCodes);
    const eligibleRoles = normalizeCodes(policy.eligibleRoleCodes);
    if (roleBased) {
      if (policy.scopeCode !== 'step')
        return 'Role assignment must apply to a work step.';
      if (
        policy.initialQueueCode ||
        eligibleQueues.length ||
        policy.unassignedBehaviorCode !== 'allow_unassigned' ||
        policy.fallbackBehaviorCode !== 'unassigned'
      )
        return 'Role assignment cannot require a queue. Review the assignment settings.';
      if (!eligibleRoles.length)
        return this.dashboardAssignment ? 'Choose at least one assignment role.' : 'Select at least one role that can be assigned this work.';
      const unavailable = eligibleRoles.find(
        (code) => !this.roleCodes.includes(code),
      );
      return unavailable
        ? `Role '${unavailable}' is unavailable. Choose an active role before applying.`
        : null;
    }
    if (eligibleQueues.length === 0) {
      return 'Choose at least one eligible queue.';
    }
    if (!policy.initialQueueCode) {
      return 'Choose an initial queue.';
    }
    if (!eligibleQueues.includes(policy.initialQueueCode)) {
      return 'The initial queue must also be selected as an eligible queue.';
    }
    const inactiveQueue = [policy.initialQueueCode, ...eligibleQueues].find(
      (code) => !this.queueCodes.includes(code),
    );
    if (inactiveQueue) {
      return `Queue '${inactiveQueue}' is not in the active authoring catalog. Choose an active queue before applying.`;
    }
    const inactiveRole = eligibleRoles.find(
      (code) => !this.roleCodes.includes(code),
    );
    if (inactiveRole) {
      return `Role '${inactiveRole}' is not in the active authoring catalog. Choose an active role before applying.`;
    }
    if (
      policy.assignmentModeCode === 'queue_only' &&
      eligibleRoles.length > 0
    ) {
      return 'Queue-only mode cannot define roles for individual assignment.';
    }
    if (
      policy.assignmentModeCode !== 'queue_only' &&
      eligibleRoles.length === 0
    ) {
      return 'Claim and direct-assignment modes require at least one eligible role.';
    }
    if (policy.unassignedBehaviorCode === 'require_queue') {
      if (
        policy.fallbackBehaviorCode === 'unassigned' ||
        policy.returnBehaviorCode === 'unassigned' ||
        policy.reentryBehaviorCode === 'unassigned'
      ) {
        return 'Fallback, return, and re-entry cannot clear the queue when queue posture is required.';
      }
    }
    return null;
  }

  private withStepReference(
    document: WorkflowDefinitionDocument,
    policyId: string | null,
  ): WorkflowDefinitionDocument {
    return {
      ...document,
      nodes: document.nodes.map((node) =>
        node?.id === this.stepNodeId && node.declaration?.behavior
          ? {
              ...node,
              declaration: {
                ...node.declaration,
                behavior: {
                  ...node.declaration.behavior,
                  assignmentPolicyId: policyId,
                },
              },
            }
          : node,
      ),
    };
  }

  private nextPolicyId(
    scopeCode: WorkflowAssignmentPolicyScopeCode,
    nodeId: string | null,
  ): string {
    const base =
      scopeCode === 'workflow'
        ? 'assignment.workflow'
        : `assignment.${nodeId || 'step'}`;
    const ids = new Set(
      this.policyRows.map((row) => row.policy?.id).filter(Boolean),
    );
    let candidate = base;
    let suffix = 2;
    while (ids.has(candidate)) {
      candidate = `${base}.${suffix++}`;
    }
    return candidate;
  }

  private setDirty(value: boolean): void {
    if (this.formDirty() === value) {
      return;
    }
    this.formDirty.set(value);
    this.pendingChangesChange.emit(value);
  }

  private showError(message: string): void {
    this.localError.set(message);
    this.scheduleFocus(() => this.policyError?.nativeElement);
  }
}

function normalizeCodes(
  values: Array<string | null> | null | undefined,
): string[] {
  return [
    ...new Set(
      (Array.isArray(values) ? values : [])
        .filter((value): value is string => typeof value === 'string')
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  ];
}

function isPolicy(
  value: WorkflowAssignmentPolicy | null,
): value is WorkflowAssignmentPolicy {
  return value !== null;
}

const assignmentFields = [
  ['id', 'Stable policy ID'],
  ['scopeCode', 'Saved attachment scope'],
  ['nodeId', 'Saved step node ID'],
  ['assignmentBasisCode', 'Saved assignment basis'],
  ['initialQueueCode', 'Default team queue'],
  ['eligibleQueueCodes', 'Eligible team queues'],
  ['eligibleRoleCodes', 'Eligible roles'],
  ['assignmentModeCode', 'How staff receive work'],
  ['unassignedBehaviorCode', 'Unassigned posture'],
  ['fallbackBehaviorCode', 'Entry fallback'],
  ['returnBehaviorCode', 'Return behavior'],
  ['reentryBehaviorCode', 'Re-entry/reset behavior'],
] as const;

function assignmentRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}
function assignmentPresence(
  value: Record<string, unknown>,
  key: string,
): 'absent' | 'null' | 'value' {
  return !Object.hasOwn(value, key)
    ? 'absent'
    : value[key] === null
      ? 'null'
      : 'value';
}
function savedAssignmentValue(value: unknown): string {
  if (value === undefined) return 'Not declared';
  if (value === null) return 'Explicitly null';
  if (typeof value === 'string')
    return value === '' ? 'Empty saved text' : value;
  try {
    return JSON.stringify(value) ?? 'Unsupported saved value';
  } catch {
    return 'Unsupported saved value';
  }
}
function assignmentShapeIssues(raw: unknown): string[] {
  if (!assignmentRecord(raw))
    return [
      'This saved policy is not a record. Its original value is retained without inventing an empty policy.',
    ];
  const issues: string[] = [];
  if (
    Object.keys(raw).some(
      (key) => !assignmentFields.some(([name]) => name === key),
    )
  )
    issues.push('Additional saved policy properties are retained read-only.');
  if (raw['scopeCode'] !== 'workflow' && raw['scopeCode'] !== 'step')
    issues.push(
      'The saved attachment scope is unsupported. No workflow or step owner is guessed.',
    );
  if (
    raw['assignmentBasisCode'] != null &&
    raw['assignmentBasisCode'] !== 'roles'
  )
    issues.push(
      'The saved assignment basis is unsupported. No queue or role basis is substituted.',
    );
  for (const [key] of assignmentFields) {
    const value = raw[key];
    if (key === 'eligibleQueueCodes' || key === 'eligibleRoleCodes') {
      if (
        value != null &&
        (!Array.isArray(value) ||
          value.some((code) => code !== null && typeof code !== 'string'))
      )
        issues.push(
          `The saved ${key} is unsupported. Its literal values are preserved.`,
        );
    } else if (value != null && typeof value !== 'string')
      issues.push(
        `The saved ${key} is not text. Its original value is preserved.`,
      );
  }
  return issues;
}
function assignmentAttachment(
  raw: unknown,
  document: WorkflowDefinitionDocument | null,
): { label: string; issues: readonly string[] } {
  if (!assignmentRecord(raw))
    return { label: `Saved policy: ${savedAssignmentValue(raw)}`, issues: [] };
  if (raw['scopeCode'] === 'workflow')
    return { label: 'Whole workflow', issues: [] };
  if (raw['scopeCode'] !== 'step')
    return {
      label: `Unsupported saved scope: ${savedAssignmentValue(raw['scopeCode'])}`,
      issues: ['The saved scope does not identify an actual policy owner.'],
    };
  const rawNodes = document?.nodes;
  const nodes = Array.isArray(rawNodes) ? rawNodes : [];
  const matches =
    typeof raw['nodeId'] === 'string' && raw['nodeId'].trim()
      ? nodes.filter(
          (node) => assignmentRecord(node) && node['id'] === raw['nodeId'],
        )
      : [];
  if (matches.length !== 1 || matches[0].typeCode !== 'human_step')
    return {
      label: `Unresolved saved step: ${savedAssignmentValue(raw['nodeId'])}`,
      issues: [
        'The saved step ID is missing, duplicated or not a human step. No first matching caption is used.',
      ],
    };
  const node = matches[0];
  return {
    label:
      typeof node.displayName === 'string' && node.displayName
        ? node.displayName
        : typeof node.stepCode === 'string' && node.stepCode
          ? node.stepCode
          : savedAssignmentValue(raw['nodeId']),
    issues: [],
  };
}
